import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { useAdminUserLinks } from '../hooks/useAdminUserLinks'
import { Button } from '@/components/ui/button'
import { SearchSingleSelect } from '@/components/common/SearchSingleSelect'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { typedDoc, updateDocument } from '@/lib/firebase/firestore'
import type { UserDocument } from '@/types'

export function AdminUserLinksPage() {
  const { user } = useAuth()
  const { notifications } = useNotificationsContext()
  const {
    users,
    members,
    loading,
    error,
    integrityIssues,
    link,
    unlink,
    backfillProfiles,
    repairLink,
    resolveNotification,
    refresh,
  } = useAdminUserLinks()

  const [selectedUserId, setSelectedUserId] = useState('')
  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null)
  const [backfilling, setBackfilling] = useState(false)
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null)

  async function handleRoleChange(userId: string, role: UserDocument['role']) {
    setRoleUpdating(userId)
    try {
      await updateDocument(typedDoc<UserDocument>('users', userId), { role })
      await refresh()
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Failed to update role'))
    } finally {
      setRoleUpdating(null)
    }
  }

  useEffect(() => {
    void refresh()
  }, [refresh])

  const pendingSuggestions = notifications.filter(
    (n) => n.type === 'member_link_suggestion' && !n.resolved
  )

  const linkedMembers = members.filter((m) => !!m.linkedUserId)
  const unlinkedUsers = users.filter((u) => !u.linkedMemberId)
  const unlinkedMembers = members.filter((m) => !m.linkedUserId)

  const userLabel = (u: typeof users[number]) => {
    const provider = u.providerIds[0]?.replace('.com', '') ?? 'unknown'
    const name = u.displayName ?? '(no name)'
    const email = u.email ?? '(no email)'
    return `${name} — ${email} [${provider}]`
  }

  const userItems = unlinkedUsers.map((u) => ({
    id: u.id,
    label: userLabel(u),
  }))

  const memberItems = unlinkedMembers.map((m) => ({
    id: m.id,
    label: m.name,
  }))

  async function handleConfirmLink(
    suggestionMemberId: string,
    suggestionUserId: string,
    notificationId: string
  ) {
    setActionError(null)
    try {
      await link(suggestionUserId, suggestionMemberId)
      if (user) await resolveNotification(notificationId, user.uid)
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Action failed'))
    }
  }

  async function handleDismiss(notificationId: string) {
    if (!user) return
    setActionError(null)
    try {
      await resolveNotification(notificationId, user.uid)
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Action failed'))
    }
  }

  async function handleUnlink(userId: string, memberId: string) {
    setActionError(null)
    try {
      await unlink(userId, memberId)
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Action failed'))
    }
  }

  async function handleManualLink() {
    if (!selectedUserId || !selectedMemberId) return
    setActionError(null)
    try {
      await link(selectedUserId, selectedMemberId)
      setSelectedUserId('')
      setSelectedMemberId('')
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Action failed'))
    }
  }

  async function handleBackfill() {
    setActionError(null)
    setBackfillStatus(null)
    setBackfilling(true)
    try {
      const result = await backfillProfiles()
      setBackfillStatus(
        `Scanned ${result.scanned} auth users — created ${result.created} missing profile${result.created === 1 ? '' : 's'} (${result.alreadyPresent} already present).`
      )
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Backfill failed'))
    } finally {
      setBackfilling(false)
    }
  }

  async function handleRepair(userId: string, memberId: string) {
    setActionError(null)
    try {
      await repairLink(userId, memberId)
    } catch (err) {
      setActionError(formatUserFacingError(err, 'Repair failed'))
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">User Links</h1>
        <p className="text-muted-foreground">Loading…</p>
      </div>
    )
  }

  return (
    <div className="space-y-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">User Links</h1>
          <p className="text-muted-foreground mt-1">
            Associate company member records with Firebase Auth accounts.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button variant="outline" size="sm" disabled={backfilling} onClick={() => void handleBackfill()}>
            {backfilling ? 'Backfilling…' : 'Backfill missing profiles'}
          </Button>
          {backfillStatus && (
            <p className="text-xs text-muted-foreground max-w-xs text-right">{backfillStatus}</p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      {/* ── Integrity Issues ───────────────────────────────────────────────── */}
      {integrityIssues.length > 0 && (
        <section className="space-y-3 rounded border border-destructive/40 bg-destructive/5 p-4">
          <h2 className="text-lg font-semibold text-destructive">Link Integrity Issues</h2>
          <p className="text-sm text-muted-foreground">
            One side of a user↔member link is missing or inconsistent. Repair re-asserts both pointers.
          </p>
          <ul className="space-y-2">
            {integrityIssues.map((issue, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 rounded border bg-background p-2 text-sm"
              >
                <span>{withErrorCode(issue.detail, `admin/user-link/${issue.kind}`)}</span>
                {issue.userId && issue.memberId && issue.kind !== 'user_points_to_missing_member' && issue.kind !== 'member_points_to_missing_user' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void handleRepair(issue.userId!, issue.memberId!)}
                  >
                    Repair
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Pending Suggestions ───────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Pending Suggestions</h2>
        {pendingSuggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending suggestions.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>User Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pendingSuggestions.map((n) => {
                const data = n.data as {
                  memberId: string
                  memberName: string
                  userId: string
                  userEmail: string
                }
                return (
                  <TableRow key={n.id}>
                    <TableCell className="font-medium">{data.memberName}</TableCell>
                    <TableCell className="text-muted-foreground">{data.userEmail}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          onClick={() =>
                            void handleConfirmLink(data.memberId, data.userId, n.id)
                          }
                        >
                          Confirm Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void handleDismiss(n.id)}
                        >
                          Dismiss
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {/* ── Current Links ─────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Current Links</h2>
        {linkedMembers.length === 0 ? (
          <p className="text-sm text-muted-foreground">No members are currently linked.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead>Linked User</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linkedMembers.map((m) => {
                const linkedUser = users.find((u) => u.id === m.linkedUserId)
                return (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {linkedUser
                        ? `${linkedUser.displayName ?? '(no name)'} — ${linkedUser.email ?? '(no email)'}`
                        : m.linkedUserId}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void handleUnlink(m.linkedUserId!, m.id)}
                      >
                        Unlink
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </section>

      {/* ── User Roles ────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">User Roles</h2>
        <p className="text-sm text-muted-foreground">
          Assign roles to control access. Designers can manage image slots on the site. Admins have full access.
          Users without a Firestore profile (no role shown) need a backfill first.
        </p>
        {users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No users found.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead className="w-40">Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    {u.displayName ?? '(no name)'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.email ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {u.providerIds.join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {u.hasProfile && u.role ? (
                      <Select
                        value={u.role}
                        disabled={roleUpdating === u.id}
                        onValueChange={(value) =>
                          void handleRoleChange(u.id, value as UserDocument['role'])
                        }
                      >
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="designer">Designer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">no profile</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      {/* ── Manual Link ───────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">Manual Link</h2>
        <p className="text-sm text-muted-foreground">
          Manually associate an unlinked user account with an unlinked member record.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-96">
            <SearchSingleSelect
              label="User"
              placeholder="Search by name or email…"
              items={userItems}
              value={selectedUserId}
              onChange={setSelectedUserId}
            />
          </div>

          <div className="w-64">
            <SearchSingleSelect
              label="Member"
              placeholder="Search by name…"
              items={memberItems}
              value={selectedMemberId}
              onChange={setSelectedMemberId}
            />
          </div>

          <Button
            className="mb-0.5"
            disabled={!selectedUserId || !selectedMemberId}
            onClick={() => void handleManualLink()}
          >
            Link
          </Button>
        </div>
      </section>
    </div>
  )
}
